import { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
// @mui
import { useTheme } from '@mui/material/styles';
import { Box, Card, CardActionArea, Typography } from '@mui/material';
// components
import { useSnackbar } from 'notistack';
import { CarouselDots } from '../../components/carousel';
import Image from '../../components/Image';
import { useDispatch, useSelector } from '../../redux/store';
import useResponsive from '../../hooks/useResponsive';
import conObj from '../../utils/connection-assistant';
import { claimPromotionBanner, getUserMemberBalance } from '../../redux/slices/lookup';
import useLocales from '../../hooks/useLocales';
import { openConfirm } from '../../redux/slices/confirm';

// ----------------------------------------------------------------------

// const carouselItems = [
//   `${S3_ASSETS_URL}home/banners/MBanner-Casino.webp`,
//   `${S3_ASSETS_URL}home/banners/MBanner-Slot.webp`,
//   `${S3_ASSETS_URL}home/banners/MBanner-Sport.webp`,
//   `${S3_ASSETS_URL}home/banners/MBanner-Fish.webp`,
// ];

// ----------------------------------------------------------------------

BannerCarousel.propTypes = {
  list: PropTypes.array,
};

export default function BannerCarousel({ ...other }) {
  const theme = useTheme();

  const { siteSettings } = useSelector((x) => x.lookup);

  const isSm = useResponsive('down', 'sm');

  const carouselRef = useRef(null);

  const [currentIndex, setCurrentIndex] = useState(0);

  const list = useMemo(() => {
    const sliders = siteSettings?.sliders;
    if (sliders?.length) {
      return sliders.map((slide) => ({
        ...slide?.BannerSlide,
        img: isSm && slide?.BannerSlide?.banner_mobile ? slide?.BannerSlide?.banner_mobile : slide?.BannerSlide?.banner,
      }));
    }

    return [];
  }, [siteSettings, isSm]);

  const settings = {
    speed: 800,
    dots: true,
    arrows: false,
    autoplay: true,
    fade: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    rtl: Boolean(theme.direction === 'rtl'),
    beforeChange: (current, next) => setCurrentIndex(next),
    ...CarouselDots({
      zIndex: 9,
      position: 'absolute',
      bottom: '10px',
      width: '100%',
      color: '#fff',
      gap: '8px',
      sx: {
        '& li': {
          width: 'unset',
          height: 'unset',
        },
      },
    }),
  };

  // const handlePrevious = () => {
  //   carouselRef.current?.slickPrev();
  // };

  // const handleNext = () => {
  //   carouselRef.current?.slickNext();
  // };

  return (
    <Card
      sx={{
        backgroundColor: 'red',
        '& .slick-active': { zIndex: 999 },
        '& .slick-active .dotActive': { transform: 'scale(1.5)' },
        borderRadius: 0,
      }}
      {...other}
    >
      <Slider ref={carouselRef} {...settings}>
        {list.map((data, index) => (
          <CarouselItem key={data?.id} data={data} isActive={index === currentIndex} />
        ))}
      </Slider>

      {/* <CarouselArrows
        onNext={handleNext}
        onPrevious={handlePrevious}
        spacing={0}
        sx={{
          top: 16,
          right: 16,
          position: 'absolute',
          '& .arrow': {
            p: 0,
            width: 32,
            height: 32,
            opacity: 0.48,
            color: 'common.white',
            '&:hover': { color: 'common.white', opacity: 1 },
          },
        }}
      /> */}
    </Card>
  );
}

// ----------------------------------------------------------------------

CarouselItem.propTypes = {
  isActive: PropTypes.bool,
  item: PropTypes.shape({
    description: PropTypes.string,
    image: PropTypes.string,
    title: PropTypes.string,
  }),
};

function CarouselItem({ data, isActive }) {
  const isAuthenticated = localStorage.getItem('accessToken');

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const { enqueueSnackbar } = useSnackbar();

  const { translate } = useLocales();

  const isClickable = useMemo(() => data?.is_manual_claimable || data?.url, [data?.is_manual_claimable, data?.url]);

  const isShowClaimedOverlay = useMemo(
    () => data?.is_manual_claimable && !data?.is_claimable_now && !data?.url,
    [data?.is_claimable_now, data?.is_manual_claimable, data?.url]
  );

  const handleClaimBonus = async () => {
    try {
      await conObj.post('transactions/manual_claim_bonus.json', {
        'transaction-promotion_id': data?.promotion_id,
      });

      dispatch(claimPromotionBanner(data?.id));

      getUserMemberBalance();

      enqueueSnackbar(translate('bonus_claimed'));
    } catch (error) {
      enqueueSnackbar(error?.message, { variant: 'error' });
    }
  };

  const handleClick = () => {
    if (isActive) {
      if (data?.url) {
        window.open(data.url, '_blank');
      } else if (data?.is_manual_claimable) {
        if (!isAuthenticated) navigate('/auth/login');
        if (data?.is_claimable_now && isAuthenticated) {
          dispatch(
            openConfirm({
              title: `${translate('claim_bonus')}?`,
              subTitle: data?.title || '',
              onConfirm: handleClaimBonus,
            })
          );
        }
      } else {
        // Do nothing
      }
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {isClickable ? (
        <CardActionArea onClick={handleClick}>
          <Image
            alt={'Carousel'}
            src={data?.img}
            sx={{ cursor: isClickable ? 'pointer' : 'default' }}
            // ratio={'12 * 4'}
          />
        </CardActionArea>
      ) : (
        <Image alt={'Carousel'} src={data?.img}/>
      )}
      {isShowClaimedOverlay ? (
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            top: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0,0,0,0.5)',
          }}
        >
          <Box sx={{ borderRadius: 3, backgroundColor: 'rgba(0,0,0,0.8)', p: 3 }}>
            <Typography>{translate('claimed')}</Typography>
          </Box>
        </Box>
      ) : (
        ''
      )}
    </Box>
  );
}
